/**
 * Выбор кошелька в браузере и кошелёк, через который подключились.
 *
 * `window.ethereum` — одна переменная на все расширения: при нескольких
 * установленных они перезаписывают её друг другом. EIP-6963 решает это
 * анонсом: каждый кошелёк представляется отдельно, провайдер выбирается явно.
 */

export interface Eip1193Provider {
  request: (args: {
    method: string;
    params?: unknown[] | object;
  }) => Promise<any>;
  on?: (event: string, handler: (...args: any[]) => void) => void;
  removeListener?: (event: string, handler: (...args: any[]) => void) => void;
  providers?: Eip1193Provider[];
  isMetaMask?: boolean;
}

declare global {
  interface Window {
    ethereum?: Eip1193Provider;
  }
}

interface AnnouncedWallet {
  info: { rdns: string; name: string };
  provider: Eip1193Provider;
}

const PREFERRED_RDNS = 'io.metamask';

const announced = new Map<string, AnnouncedWallet>();

if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', (event) => {
    const detail = (event as CustomEvent<AnnouncedWallet>).detail;
    if (detail?.info?.rdns && detail?.provider) {
      announced.set(detail.info.rdns, detail);
    }
  });

  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

// Запасной путь для кошельков без поддержки EIP-6963.
const pickFromWindow = (): Eip1193Provider | null => {
  const injected = window.ethereum;
  if (!injected) return null;

  // Несколько расширений сразу — MetaMask складывает соседей в `providers`.
  if (Array.isArray(injected.providers) && injected.providers.length > 0) {
    return (
      injected.providers.find((provider) => provider.isMetaMask) ??
      injected.providers[0]
    );
  }

  return injected;
};

const pick = (): Eip1193Provider | null => {
  const preferred = announced.get(PREFERRED_RDNS);
  if (preferred) return preferred.provider;

  const first = announced.values().next().value;
  return first ? first.provider : pickFromWindow();
};

/**
 * Расширение внедряется в страницу асинхронно и на холодном старте браузера
 * успевает не всегда. Проверка сразу при загрузке дала бы ложное «кошелёк не
 * установлен» — поэтому ждём.
 */
export const waitForInjectedProvider = async (
  timeoutMs = 3000,
): Promise<Eip1193Provider | null> => {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    // Кошелёк мог подписаться на запрос позже нас — просим анонс повторно.
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    const provider = pick();
    if (provider) return provider;

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return null;
};

/**
 * Кошелёк, через который оператор подключился: расширение или WalletConnect.
 * Выставляет `WalletProvider`; подпись берёт его, а не найденное расширение —
 * иначе на компьютере с MetaMask подключение по QR подписывало бы расширением.
 */
let active: Eip1193Provider | null = null;

export const setActiveProvider = (provider: Eip1193Provider | null) => {
  active = provider;
};

export const getActiveProvider = (): Eip1193Provider | null => active;
